// The character all players are drawn with, loaded once from the player
// bundle (see server/bundles.ts buildPlayerBundle): mesh, MDX animations,
// animation script, skins, head, accessories and weapon. Shared by every
// drawn player (PlayerModel) and every map.

import type { VirtualFS } from '../fs/vfs';
import { expandIncludes, parseAnimGroup, type Animation } from './anim/animGroup';
import { parseAnimScript, type AnimModel } from './anim/animScript';
import { loadShaders } from './mapLoader';
import {
  BODY_ACCESSORIES,
  HEAD_ACCESSORIES,
  PLAYER_CHARACTER,
  PLAYER_WEAPON,
  PLAYER_WEAPON_SKIN,
  VIEW_WEAPON,
  accessorySkinPath,
  bodySkinPath,
  modelFile,
  parseCharacter,
  type CharacterDef,
} from '../render/models/character';
import { parseMd3, type MeshModel } from '../render/models/md3';
import { parseMdc } from '../render/models/mdc';
import { parseMdm, type MdmModel } from '../render/models/mdm';
import { parseMdx, type MdxModel } from '../render/models/mdx';
import { Skeleton, findMdmTag, identityAxis } from '../render/models/skeleton';
import { parseSkin, type Skin } from '../render/models/skin';
import type { ShaderDef } from '../render/shaderScripts';
import type { Vec3 } from '../shared/math';

export interface HeadAnim {
  firstFrame: number;
  numFrames: number;
  fps: number;
  loopFrames: number;
}

export interface Accessory {
  type: string;
  model: MeshModel;
  skin: Skin | null;
  /** Tag it hangs on: of the body, or of the head (onHead). */
  tag: string;
  onHead: boolean;
}

/** The knife in first person (VIEW_WEAPON). */
export interface ViewWeaponAssets {
  hands: MeshModel;
  model: MeshModel;
  /** The arm's skin per team (Axis, Allies). */
  skins: [Skin | null, Skin | null];
  parts: { tag: string; model: MeshModel }[];
}

export class PlayerAssets {
  private constructor(
    readonly vfs: VirtualFS,
    readonly def: CharacterDef,
    readonly mdm: MdmModel,
    readonly bodySkin: Skin | null,
    /** MDX file (lower case) -> skeleton animation. */
    readonly mdx: Map<string, MdxModel>,
    readonly animModel: AnimModel,
    readonly head: MeshModel | null,
    readonly headSkin: Skin | null,
    /** HD_IDLE1 of the head animations (small eye movements, played all the time). */
    readonly headIdle: HeadAnim | null,
    readonly accessories: Accessory[],
    readonly weapon: { model: MeshModel; skin: Skin | null } | null,
    /** Null when the player bundle has no first person knife. */
    readonly viewWeapon: ViewWeaponAssets | null,
    readonly shaders: Map<string, ShaderDef>,
  ) {}

  get animations(): Animation[] {
    return this.animModel.animations;
  }

  get numBones(): number {
    return this.mdx.values().next().value?.numBones ?? 0;
  }

  /** Loads the character from a filesystem holding the player bundle. */
  static async load(vfs: VirtualFS): Promise<PlayerAssets> {
    const text = async (path: string) => {
      const t = await vfs.readText(path);
      if (t === null) throw new Error(`${path} not found`);
      return t;
    };
    const bytes = async (path: string) => {
      const d = await vfs.read(path);
      if (!d) throw new Error(`${path} not found`);
      return d;
    };
    const optionalSkin = async (path: string) => {
      const t = await vfs.readText(path);
      return t === null ? null : parseSkin(t);
    };
    const mesh = async (name: string) => {
      const path = modelFile((p) => vfs.has(p), name);
      if (!path) return null;
      const data = await bytes(path);
      return path.endsWith('.mdc') ? parseMdc(data, path) : parseMd3(data, path);
    };

    const def = parseCharacter(await text(PLAYER_CHARACTER));
    const mdm = parseMdm(await bytes(def.mesh), def.mesh);
    const bodySkin = await optionalSkin(bodySkinPath(def));

    const animations = parseAnimGroup(await expandIncludes(vfs, def.animationGroup));
    const mdx = new Map<string, MdxModel>();
    for (const a of animations) if (!mdx.has(a.mdxFile)) mdx.set(a.mdxFile, parseMdx(await bytes(a.mdxFile), a.mdxFile));
    calcMoveSpeeds(animations, mdm, mdx);
    const animModel = parseAnimScript(await text(def.animationScript), animations, def.animationScript);

    const head = def.hudHead ? await mesh(def.hudHead) : null;
    const headSkin = def.hudHeadSkin ? await optionalSkin(def.hudHeadSkin) : null;
    const headIdle = def.hudHeadAnims ? parseHeadAnims((await vfs.readText(def.hudHeadAnims)) ?? '')[0] ?? null : null;

    const accessories: Accessory[] = [];
    for (const [type, name] of bodySkin?.models ?? []) {
      const tag = BODY_ACCESSORIES[type] ?? HEAD_ACCESSORIES[type];
      if (!tag) continue;
      const model = await mesh(name);
      if (model) accessories.push({ type, model, skin: await optionalSkin(accessorySkinPath(name, def.skin)), tag, onHead: type in HEAD_ACCESSORIES });
    }
    const weaponModel = await mesh(PLAYER_WEAPON);
    const weapon = weaponModel ? { model: weaponModel, skin: await optionalSkin(PLAYER_WEAPON_SKIN) } : null;

    const hands = await mesh(VIEW_WEAPON.hands);
    const viewModel = await mesh(VIEW_WEAPON.model);
    let viewWeapon: ViewWeaponAssets | null = null;
    if (hands && viewModel) {
      const parts: ViewWeaponAssets['parts'] = [];
      for (const p of VIEW_WEAPON.parts) {
        const model = await mesh(p.model);
        if (model) parts.push({ tag: p.tag, model });
      }
      viewWeapon = { hands, model: viewModel, skins: [await optionalSkin(VIEW_WEAPON.skins[0]), await optionalSkin(VIEW_WEAPON.skins[1])], parts };
    }

    return new PlayerAssets(vfs, def, mdm, bodySkin, mdx, animModel, head, headSkin, headIdle, accessories, weapon, viewWeapon, await loadShaders(vfs));
  }
}

/** animations/human/heads/*.anim: "first length fps looping" per line. */
export function parseHeadAnims(text: string): HeadAnim[] {
  const out: HeadAnim[] = [];
  for (const line of text.split(/\r?\n/)) {
    const m = /^\s*(\d+)\s+(\d+)\s+(\d+)\s+(\d+)/.exec(line.replace(/\/\/.*/, ''));
    if (m) out.push({ firstFrame: +m[1], numFrames: +m[2], fps: +m[3] || 1, loopFrames: +m[4] });
  }
  return out;
}

/**
 * CG_CalcMoveSpeeds: animations made with a move speed of -1 get the speed
 * their contact foot slides back at (along x; along z for ladder animations).
 * ET reads the previous foot position of the first frame from uninitialized
 * memory; here it is the last frame of the cycle.
 */
export function calcMoveSpeeds(animations: Animation[], mdm: MdmModel, mdx: Map<string, MdxModel>): void {
  const feet = [findMdmTag(mdm, 'tag_footleft'), findMdmTag(mdm, 'tag_footright')];
  if (!feet[0] || !feet[1]) return;
  let skeleton: Skeleton | null = null;
  const footPositions = (model: MdxModel, frame: number): [Vec3, Vec3] => {
    skeleton ??= new Skeleton(model.numBones);
    skeleton.calc({ frameModel: model, frame, oldFrameModel: model, oldFrame: frame, backlerp: 0, torsoFrameModel: model, torsoFrame: frame, oldTorsoFrameModel: model, oldTorsoFrame: frame, torsoBacklerp: 0, torsoAxis: identityAxis() });
    return [skeleton.tag(feet[0]!).origin, skeleton.tag(feet[1]!).origin];
  };
  for (const anim of animations) {
    if (anim.moveSpeed >= 0) continue;
    const model = mdx.get(anim.mdxFile);
    if (!model || anim.numFrames < 1) {
      anim.moveSpeed = 0;
      continue;
    }
    const ladder = anim.name.startsWith('climb');
    let total = 0;
    let old = footPositions(model, anim.firstFrame + anim.numFrames - 1);
    for (let j = 0; j < anim.numFrames; j++) {
      const o = footPositions(model, anim.firstFrame + j);
      if (ladder) {
        const low = o[0][0] > o[1][0] ? 0 : 1;
        total += Math.abs(old[low][2] - o[low][2]);
      } else {
        const low = o[0][2] < o[1][2] ? 0 : 1;
        total += Math.abs(old[low][0] - o[low][0]);
      }
      old = o;
    }
    anim.moveSpeed = Math.trunc(((total / anim.numFrames) * 1000) / anim.frameLerp);
  }
}
